import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  PeopleAlt as PeopleAltIcon,
  HowToReg as HowToRegIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { baseClient } from '../api/baseClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AffiliateStats {
  total_referrals: number;
  signups: number;
  conversions: number;
  total_earned: number;
}

interface AffiliateProfile {
  id: string;
  user_id: string;
  name: string;
  email: string | null;
  payout_email: string | null;
  referral_code: string;
  commission_rate: number;
  status: string;
  created_at: string | null;
  stats: AffiliateStats;
}

interface Referral {
  id: string;
  referred_email: string | null;
  referred_user_id: string | null;
  status: string;
  product: string | null;
  revenue_amount: number | null;
  commission_amount: number | null;
  converted_at: string | null;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => (
  <Card elevation={2} sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ color, mr: 1 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" fontWeight="bold">
        {value}
      </Typography>
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Status chip helper
// ---------------------------------------------------------------------------

const statusColor = (s: string): 'default' | 'success' | 'warning' | 'info' => {
  switch (s) {
    case 'converted':
      return 'success';
    case 'signed_up':
      return 'info';
    default:
      return 'default';
  }
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ReferralPage: React.FC = () => {
  const [profile, setProfile] = useState<AffiliateProfile | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  // Registration form
  const [regName, setRegName] = useState('');
  const [regPayoutEmail, setRegPayoutEmail] = useState('');
  const [regError, setRegError] = useState<string | null>(null);

  // Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await baseClient.get<AffiliateProfile>('/affiliates/me');
      setProfile(data);
      await fetchReferrals();
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // Not registered yet – that's fine
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      const data = await baseClient.get<Referral[]>('/affiliates/referrals');
      setReferrals(data);
    } catch {
      setReferrals([]);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName.trim()) {
      setRegError('Name is required.');
      return;
    }
    setRegError(null);
    setRegistering(true);
    try {
      await baseClient.post('/affiliates/register', {
        name: regName.trim(),
        payout_email: regPayoutEmail.trim() || undefined,
      });
      await fetchProfile();
      showSnack('You are now registered as an affiliate!');
    } catch (err: any) {
      setRegError(
        err?.response?.data?.detail || 'Registration failed. Please try again.'
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleCopyLink = () => {
    if (!profile) return;
    const link = `${window.location.origin}/?ref=${profile.referral_code}`;
    navigator.clipboard.writeText(link).then(() => {
      showSnack('Referral link copied to clipboard!');
    });
  };

  const showSnack = (msg: string) => {
    setSnackMsg(msg);
    setSnackOpen(true);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Not registered ---
  if (!profile) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6, px: 2 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Join the Referral Program
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Earn a 20% commission for every customer you refer. Register below to
          get your unique referral link.
        </Typography>

        <Card elevation={3}>
          <CardContent>
            <Box component="form" onSubmit={handleRegister}>
              {regError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {regError}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Your Name"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Payout Email (optional)"
                type="email"
                value={regPayoutEmail}
                onChange={(e) => setRegPayoutEmail(e.target.value)}
                margin="normal"
                helperText="Where should commission payouts be sent?"
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={registering}
                sx={{ mt: 2, py: 1.5, textTransform: 'none' }}
              >
                {registering ? 'Registering…' : 'Register as Affiliate'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Snackbar
          open={snackOpen}
          autoHideDuration={4000}
          onClose={() => setSnackOpen(false)}
          message={snackMsg}
        />
      </Box>
    );
  }

  // --- Registered ---
  const referralLink = `${window.location.origin}/?ref=${profile.referral_code}`;
  const { stats } = profile;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', px: 2, py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PeopleAltIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" fontWeight="bold">
          Referral Dashboard
        </Typography>
        <Chip
          label={profile.status}
          color={profile.status === 'active' ? 'success' : 'default'}
          size="small"
          sx={{ ml: 2 }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back, {profile.name}. Commission rate: {profile.commission_rate}%
      </Typography>

      {/* Referral link */}
      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Your Referral Link
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              value={referralLink}
              InputProps={{ readOnly: true }}
              size="small"
              variant="outlined"
            />
            <Tooltip title="Copy link">
              <IconButton onClick={handleCopyLink} color="primary">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Code: <strong>{profile.referral_code}</strong>
          </Typography>
        </CardContent>
      </Card>

      {/* Stat cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Clicks / Referrals"
            value={stats.total_referrals}
            icon={<PeopleAltIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Signups"
            value={stats.signups}
            icon={<HowToRegIcon />}
            color="#0288d1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Conversions"
            value={stats.conversions}
            icon={<CheckCircleIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Earned"
            value={`$${stats.total_earned.toFixed(2)}`}
            icon={<AttachMoneyIcon />}
            color="#e65100"
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Referrals table */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Referral Activity
      </Typography>

      {referrals.length === 0 ? (
        <Alert severity="info">
          No referrals yet. Share your referral link to get started!
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email / User ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Commission</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referrals.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    {r.referred_email || r.referred_user_id || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.status}
                      color={statusColor(r.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{r.product || '—'}</TableCell>
                  <TableCell align="right">
                    {r.revenue_amount != null
                      ? `$${r.revenue_amount.toFixed(2)}`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {r.commission_amount != null
                      ? `$${r.commission_amount.toFixed(2)}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {r.created_at
                      ? new Date(r.created_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
      />
    </Box>
  );
};

export default ReferralPage;
